import React, { useEffect, useMemo, useState } from "react";

// Minimal inline SVG loader that is theme-aware via CSS `color`.
// It fetches `/icons/<name>.svg` from the public dir and injects it inline.
// NOTE: Only use with our own trusted icons.
const cache = new Map<string, string>();

function normalizeSvgToCurrentColor(svg: string): string {
  // Force icons to follow text color. We keep viewBox and sizing from the source.
  // Remove hardcoded fills/strokes (except none) and set to currentColor.
  let out = svg;

  // Strip XML/doctype
  out = out.replace(/<\?xml[^>]*>\s*/g, "");
  out = out.replace(/<!doctype[^>]*>\s*/gi, "");

  // Ensure the root svg has a fill default (do NOT force stroke)
  out = out.replace(
    /<svg\b([^>]*)>/i,
    (_m, attrs) => {
      const hasFill = /\sfill=/.test(attrs);
      const fillAttr = hasFill ? "" : ' fill="currentColor"';
      return `<svg${attrs}${fillAttr}>`;
    }
  );

  // Replace explicit fills/strokes that are not "none"
  out = out.replace(/\sfill="(?!none")[^"]*"/gi, ' fill="currentColor"');
  out = out.replace(/\sstroke="(?!none")[^"]*"/gi, ' stroke="currentColor"');

  return out;
}

export function SvgIcon(props: {
  name: string; // e.g. "reply" for /icons/reply.svg
  title?: string; // accessible title (optional)
  className?: string;
  style?: React.CSSProperties;
}) {
  const src = useMemo(() => `/icons/${props.name}.svg`, [props.name]);
  const [svg, setSvg] = useState<string | null>(cache.get(src) ?? null);

  useEffect(() => {
    let alive = true;
    const cached = cache.get(src);
    if (cached) {
      setSvg(cached);
      return;
    }

    (async () => {
      try {
        const res = await fetch(src, { cache: "force-cache" });
        if (!res.ok) throw new Error(`Failed to load ${src}`);
        const raw = await res.text();
        const normalized = normalizeSvgToCurrentColor(raw);
        cache.set(src, normalized);
        if (alive) setSvg(normalized);
      } catch {
        if (alive) setSvg(null);
      }
    })();

    return () => {
      alive = false;
    };
  }, [src]);

  if (!svg) {
    // fallback box (keeps layout stable)
    return <span className={props.className} aria-hidden="true" style={{ display: "inline-block", width: 16, height: 16, ...props.style }} />;
  }

  return (
    <span
      className={props.className}
      style={props.style}
      role="img"
      aria-label={props.title ?? props.name}
      title={props.title ?? props.name}
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
